import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import { IconWrapper } from '../IconWrapper/Index';

interface Iprops {
  variant:
    | 'wifi'
    | 'globe'
    | 'class'
    | 'connected'
    | 'connectedmobile'
    | 'connectedmobile2';
  fullwidth?: boolean;
  isOnline?: boolean;
  /** While question session API is in progress — show loading, not disconnected */
  isLoading?: boolean;
  /** Shown on an info (i) button when offline due to a specific mismatch */
  disconnectReason?: string;
  setInstructionModal?: Dispatch<SetStateAction<boolean>>;
  instructionEvent?: () => void;
  totalJoined?: number;
  totalStudents?: number;
  totalAnswered?: number;
  correct?: number;
  averageTime?: number;
  averagemarks?: number | string;
  // Lets a caller running a different live-session flavor (e.g. the
  // web-only LiveQuiz feature, which has no physical base station) reuse
  // this exact bar instead of a lookalike — defaults preserve every
  // existing Clicker call site byte-for-byte.
  label?: string;
  offlineLabel?: string;
}
const styles: IStyles = {
  item: {
    // width: { xs: '100%', md: 'unset' },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: pxTovW(5),
    '&>div': {
      display: 'flex',
      gap: pxTovW(5),
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
};

export const ConnectedClassIndicator = (props: Iprops) => {
  const {
    variant,
    fullwidth,
    isOnline,
    isLoading,
    disconnectReason,
    setInstructionModal,
    instructionEvent,
    totalJoined,
    totalStudents,
    totalAnswered,
    correct,
    averageTime,
    averagemarks,
    label = 'Connected Class',
    offlineLabel,
  } = props;

  const styles: IStyles = {
    default: {
      width: fullwidth
        ? { xs: '100vw', md: '100%' }
        : { xs: pxToRem(472), md: pxTovW(472) },
      height: { xs: pxToRem(29), md: pxTovW(74) },
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      // borderRadius: { xs: pxToRem(18), md: pxTovW(18) },
      gap: { xs: pxToRem(25), md: pxTovW(25) },
      background:
        'transparent linear-gradient(270deg, #F1F0F6 0%, #FAEDEE 69%, #FCECED 100%) 0% 0% no-repeat padding-box;',
      border: '1px solid #007CDC',
      // opacity: 1
      boxSizing: 'border-box',
    },
    // item: {
    //   display: 'flex',
    //   flexDirection: 'column',
    //   justifyContent: 'center',
    //   alignItems: 'center',
    //   gap: pxTovW(5),
    //   '&>div': {
    //     display: 'flex',
    //     gap: pxTovW(5),
    //     justifyContent: 'center',
    //     alignItems: 'center',
    //   },
    // },
  };

  if (isLoading)
    return (
      <LoadingVariant
        largeHeightVariant={variant === 'connected'}
        fullwidth={fullwidth}
      />
    );

  if (!isOnline)
    return (
      <OfflineVariant
        largeHeightVariant={variant === 'connected' ? true : false}
        fullwidth={fullwidth}
        label={offlineLabel}
        disconnectReason={disconnectReason}
      />
    );

  return (
    <Box>
      {variant === 'connected' ? (
        <Box
          sx={{
            ...styles.default,
            height: { md: pxTovW(118) },
            // width: { md: '95%', lg: pxTovW(1441) },
            width: '100%',
            paddingX: { xs: pxToRem(0), md: pxToRem(20) },
            marginX: 'auto',
            background: '#F3FAFF',
            border: '1px solid #007CDC96',
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              width: { md: pxTovW(480) },
              height: '100%',
              background: '#007CDC',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: pxToRem(5), md: pxTovW(25) },
            }}
          >
            <IconWrapper
              name="Wifi@2x"
              parentFolder="icons"
              size="md"
              customSx={{
                width: { xs: pxToRem(15), md: pxTovW(48) },
                height: { xs: pxToRem(15), md: pxTovW(40) },
              }}
              type="png"
            />
            <Typography
              fontFamily={'poppins'}
              fontSize={{ xs: pxToRem(18), md: pxTovW(32) }}
              fontWeight={600}
              color={'#FFFFFF'}
            >
              {label}
            </Typography>
          </Box>
          <Box
            sx={{
              width: { md: pxTovW(818) },
              display: 'flex',
              gap: { md: pxTovW(30) },
              marginRight: { md: pxTovW(30) },
            }}
          >
            <StudentInfoBox
              totalJoined={totalJoined}
              totalStudents={totalStudents}
              totalAnswered={totalAnswered}
              correct={correct}
            />
            <MarksInfoBox
              averageTime={averageTime}
              averageMarks={averagemarks}
            />
          </Box>
        </Box>
      ) : variant === 'connectedmobile' ? (
        <Box
          sx={{
            ...styles.default,
            background: '#F3FAFF',
            border: '1px solid #007CDC96',
            display: 'flex',
            justifyContent: 'space-around',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: pxToRem(5), md: pxTovW(25) },
            }}
          >
            <IconWrapper
              name="wifi"
              parentFolder="icons"
              size="md"
              customSx={{
                width: { xs: pxToRem(15), md: pxTovW(44) },
                height: { xs: pxToRem(15), md: pxTovW(44) },
              }}
              type="png"
            />
            <Typography
              fontFamily={'poppins'}
              fontSize={{ xs: pxToRem(18), md: pxTovW(24) }}
              fontWeight={600}
            >
              {label}
            </Typography>
          </Box>
        </Box>
      ) : variant === 'connectedmobile2' ? (
        <Box
          sx={{
            ...styles.default,
            background: '#F3FAFF',
            height: { xs: 'max-content', md: 'unset' },
            border: '1px solid #007CDC96',
            display: 'flex',
            flexDirection: 'column',
            // justifyContent: 'space-around',
            paddingBottom: { xs: pxToRem(10), md: 'unset' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: '#007CDC',
              width: { xs: '100%', md: 'unset' },
              height: { xs: pxToRem(29), md: pxTovW(74) },
              gap: { xs: pxToRem(5), md: pxTovW(25) },
            }}
          >
            <IconWrapper
              name="Wifi@2x"
              parentFolder="icons"
              size="md"
              customSx={{
                width: { xs: pxToRem(15), md: pxTovW(44) },
                height: { xs: pxToRem(15), md: pxTovW(44) },
              }}
              type="png"
            />
            <Typography
              fontFamily={'poppins'}
              fontSize={{ xs: pxToRem(18), md: pxTovW(24) }}
              fontWeight={600}
              color={'#FFFFFF'}
            >
              {label}
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: pxToRem(319), md: pxTovW(818) },
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'unset' },
              justifyContent: { xs: 'center', md: 'unset' },
              gap: { xs: pxToRem(8), md: pxTovW(30) },
              marginRight: { xs: 0, md: pxTovW(30) },
            }}
          >
            <StudentInfoBox
              totalJoined={totalJoined}
              totalStudents={totalStudents}
              totalAnswered={totalAnswered}
              correct={correct}
            />
            <MarksInfoBox
              averageTime={averageTime}
              averageMarks={averagemarks}
            />
          </Box>
        </Box>
      ) : variant === 'wifi' ? (
        <Box
          sx={{
            ...styles.default,
            background: '#F3FAFF',
            border: '1px solid #007CDC96',
            display: 'flex',
            justifyContent: 'space-around',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: pxToRem(5), md: pxTovW(25) },
            }}
          >
            <IconWrapper
              name="wifi"
              parentFolder="icons"
              size="md"
              customSx={{
                width: { xs: pxToRem(15), md: pxTovW(44) },
                height: { xs: pxToRem(15), md: pxTovW(44) },
              }}
              type="png"
            />
            <Typography
              fontFamily={'poppins'}
              fontSize={{ xs: pxToRem(18), md: pxTovW(24) }}
              fontWeight={600}
            >
              {label}
            </Typography>
          </Box>

          <Box>
            <Button
              sx={{
                backgroundColor: 'transparent',
                '& :hover': {
                  backgroundColor: 'transparent',
                },
              }}
              variant="text"
              onClick={() => {
                setInstructionModal && setInstructionModal(true);
                instructionEvent && instructionEvent();
              }}
            >
              <InfoTwoToneIcon />
              <Typography variant="linkText" color="primary" fontWeight="bold">
                Instructions
              </Typography>
            </Button>
          </Box>
        </Box>
      ) : variant === 'globe' ? (
        <Box
          sx={{
            ...styles.default,
            background: '#D9FCE9',
            border: '1px solid #0AA34F',
          }}
        >
          <IconWrapper
            name="globe"
            parentFolder="icons"
            size="md"
            customSx={{
              width: { xs: pxToRem(62), md: pxTovW(62) },
              height: { xs: pxToRem(58), md: pxTovW(58) },
            }}
            type="png"
          />
          <Typography
            fontFamily={'poppins'}
            fontSize={{ xs: pxToRem(24), md: pxTovW(24) }}
            fontWeight={600}
          >
            {label}
          </Typography>
        </Box>
      ) : variant === 'class' ? (
        <Box sx={styles.default}>
          <IconWrapper
            name="connectedclassicon"
            parentFolder="icons"
            size="md"
            customSx={{
              width: { xs: pxToRem(52), md: pxTovW(52) },
              height: { xs: pxToRem(52), md: pxTovW(52) },
            }}
            type="png"
          />
          <Typography
            fontFamily={'poppins'}
            fontSize={{ xs: pxToRem(14), md: pxTovW(24) }}
            fontWeight={600}
          >
            {label}
          </Typography>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

interface OfflineVariantProps {
  fullwidth?: boolean;
  largeHeightVariant?: boolean;
  label?: string;
  disconnectReason?: string;
}
const OfflineVariant = ({
  fullwidth,
  largeHeightVariant,
  label,
  disconnectReason,
}: OfflineVariantProps) => {
  return (
    <Box
      sx={{
        width: fullwidth
          ? { xs: '100vw', md: '100%' }
          : { xs: pxToRem(472), md: pxTovW(472) },
        height: {
          xs: pxToRem(29),
          md: largeHeightVariant ? pxTovW(118) : pxTovW(74),
        },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: { xs: pxToRem(10), md: pxTovW(25) },
        background: '#F9F9F9',
        border: '1px solid #C6C5C5',
      }}
    >
      <IconWrapper
        name="no_connection"
        parentFolder="icons"
        size="md"
        customSx={{
          width: { xs: pxToRem(20), md: pxTovW(44) },
          height: { xs: pxToRem(20), md: pxTovW(44) },
        }}
        type="png"
      />
      <Typography variant="h2">
        {label ?? 'Disconnected from the Base station'}
      </Typography>
      {disconnectReason ? (
        <Tooltip title={disconnectReason} arrow>
          <IconButton
            size="small"
            aria-label={disconnectReason}
            sx={{
              color: '#007CDC',
              p: { xs: pxToRem(2), md: pxTovW(4) },
            }}
          >
            <InfoTwoToneIcon
              sx={{
                fontSize: { xs: pxToRem(18), md: pxTovW(28) },
              }}
            />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

const LoadingVariant = ({
  fullwidth,
  largeHeightVariant,
}: {
  fullwidth?: boolean;
  largeHeightVariant?: boolean;
}) => {
  return (
    <Box
      sx={{
        width: fullwidth
          ? { xs: '100vw', md: '100%' }
          : { xs: pxToRem(472), md: pxTovW(472) },
        height: {
          xs: pxToRem(29),
          md: largeHeightVariant ? pxTovW(118) : pxTovW(74),
        },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: { xs: pxToRem(10), md: pxTovW(25) },
        background: '#F3FAFF',
        border: '1px solid #007CDC96',
      }}
    >
      <CircularProgress
        size={28}
        sx={{
          width: { xs: pxToRem(16), md: pxTovW(28) },
          height: { xs: pxToRem(16), md: pxTovW(28) },
        }}
      />
      <Typography variant="h2" color="primary.main">
        Connecting to question session...
      </Typography>
    </Box>
  );
};

interface StudentInfoBoxProps {
  totalJoined?: number;
  totalStudents?: number;
  totalAnswered?: number;
  correct?: number;
}

const StudentInfoBox = (props: StudentInfoBoxProps) => {
  const {
    totalJoined = 0,
    totalStudents = 0,
    totalAnswered = 0,
    // correct = 0,
    correct,
  } = props;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: pxTovW(431) },
        height: { md: pxTovW(91) },
        display: 'flex',
        justifyContent: 'space-around',
        background: '#FFFFFF',
        border: '1px solid #70C0FE',
        borderRadius: { xs: pxToRem(8), md: pxTovW(11) },
        padding: { xs: pxToRem(10), md: 'unset' },
      }}
    >
      <Box sx={styles.item}>
        <Box>
          <IconWrapper
            name="joined"
            size="large"
            type="png"
            parentFolder="icons"
          />
          <Typography variant="h3" fontWeight={700}>
            {totalJoined}/{totalStudents}
          </Typography>
        </Box>
        <Box>
          <Typography variant="smallText">Joined</Typography>
        </Box>
      </Box>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{ mx: 1, borderStyle: 'dashed' }}
      />
      <Box sx={styles.item}>
        <Box>
          <IconWrapper
            name="whatsapp_pink"
            size="large"
            type="png"
            parentFolder="icons"
          />
          <Typography variant="h3" fontWeight={700}>
            {totalAnswered}/{totalStudents}
          </Typography>
        </Box>
        <Box>
          <Typography variant="smallText">Answered</Typography>
        </Box>
      </Box>

      {correct !== undefined && (
        <>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ mx: 1, borderStyle: 'dashed' }}
          />

          <Box sx={styles.item}>
            <Box>
              <IconWrapper
                name="corrected"
                size="large"
                type="png"
                parentFolder="icons"
              />
              <Typography variant="h3" fontWeight={700}>
                {correct}
              </Typography>
            </Box>
            <Box>
              <Typography variant="smallText">Correct</Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
interface IMarksInfoBoxProps {
  averageTime?: number;
  averageMarks?: number | string;
}

const MarksInfoBox = (props: IMarksInfoBoxProps) => {
  const { averageTime, averageMarks } = props;
  return (
    <Box
      sx={{
        width: { xs: '100%', md: pxTovW(357) },
        height: { xs: 'max-content', md: pxTovW(91) },
        display: 'flex',
        justifyContent: { xs: 'space-around' },
        background: '#FFFFFF',
        border: '1px solid #70C0FE',
        borderRadius: { xs: pxToRem(8), md: pxTovW(11) },
        padding: { xs: pxToRem(10), md: 'unset' },
      }}
    >
      <Box sx={styles.item}>
        <Box>
          <IconWrapper
            name="average-time"
            size="large"
            type="png"
            parentFolder="icons"
          />
          <Typography variant="h3" fontWeight={700}>
            {averageTime ? averageTime : 0} secs
          </Typography>
        </Box>
        <Box>
          <Typography variant="smallText">Average Time</Typography>
        </Box>
      </Box>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{ mx: 1, borderStyle: 'dashed' }}
      />
      <Box sx={styles.item}>
        <Box>
          <IconWrapper
            name="average-score"
            size="large"
            type="png"
            parentFolder="icons"
          />
          <Typography variant="h3" fontWeight={700}>
            {averageMarks}%
          </Typography>
        </Box>
        <Box>
          <Typography variant="smallText">Average Score</Typography>
        </Box>
      </Box>
    </Box>
  );
};
